import { useEffect } from 'react';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ClockIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import InnerPageHero from '../components/InnerPageHero';
import FAQ from '../components/FAQ';
import { useContent } from '../context/ContentContext';

function ContactInfo() {
  const { content, settings } = useContent();
  const d = content.contactInfo;

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-brand-off-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-navy/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-brand-orange/5 rounded-full blur-3xl" />

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 xl:top-48">
              <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-brand-orange rounded-full" />
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                  {d.sectionLabel}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
                {d.heading}
              </h2>

              <p className="text-base sm:text-lg text-brand-warm-gray leading-relaxed mb-8 sm:mb-10">
                {d.description}
              </p>

              <div className="space-y-4 mb-8 sm:mb-10">
                <a
                  href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-brand-orange/30 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange group-hover:scale-105 transition-all duration-300">
                    <PhoneIcon className="w-5 h-5 text-brand-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-navy">{settings.phone}</p>
                    <p className="text-brand-warm-gray text-sm">{d.phoneSubtext}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${settings.email}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-brand-orange/30 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange group-hover:scale-105 transition-all duration-300">
                    <EnvelopeIcon className="w-5 h-5 text-brand-orange group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    <p className="font-semibold text-brand-navy text-sm sm:text-base break-all">{settings.email}</p>
                    <p className="text-brand-warm-gray text-sm">{d.emailSubtext}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPinIcon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-navy">{d.address}</p>
                    <p className="text-brand-warm-gray text-sm">{d.addressSubtext}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-semibold text-brand-navy">{d.hours}</p>
                    <p className="text-brand-warm-gray text-sm">{d.hoursSubtext}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {d.stats.map((stat) => (
                  <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
                    <p className="text-xl font-bold text-brand-orange">{stat.value}</p>
                    <p className="text-brand-warm-gray text-xs mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const { content, settings } = useContent();
  const d = content.contactInfo;
  return (
    <div id="contact-form" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 sm:p-8 lg:p-10 border-b border-gray-100">
        <div className="inline-flex items-center gap-2 bg-brand-off-white rounded-full px-4 py-2 mb-4">
          <ClockIcon className="w-5 h-5 text-brand-orange" />
          <span className="text-brand-navy font-medium text-sm">{d.formBadge}</span>
        </div>
        <h3 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy mb-2">{d.formHeading}</h3>
        <p className="text-brand-warm-gray text-sm">Choose a convenient time through our online booking page, or call our team for immediate help.</p>
      </div>
      <div className="p-6 sm:p-8 lg:p-10 space-y-6">
        <div className="flex items-start gap-3">
          <CheckCircleIcon className="w-6 h-6 text-brand-orange shrink-0" />
          <p className="text-brand-warm-gray leading-relaxed">Tell us what your home needs and select an appointment with Best Neighbor Home Services.</p>
        </div>
        <a href={settings.bookingUrl} target="_blank" rel="noopener noreferrer" className="flex min-h-12 items-center justify-center gap-3 rounded-lg bg-brand-orange px-6 py-4 text-center font-semibold text-brand-navy hover:bg-brand-navy transition-colors">
          Book Your Free Estimate
          <ArrowRightIcon className="h-5 w-5" />
        </a>
        <p className="text-center text-xs text-brand-warm-gray">Opens our online booking page in a new tab.</p>
        <a href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`} className="flex min-h-12 items-center justify-center gap-3 rounded-lg border border-brand-navy px-6 py-3 font-semibold text-brand-navy hover:bg-brand-off-white">
          <PhoneIcon className="h-5 w-5" />
          {settings.phone}
        </a>
        <p className="text-sm leading-relaxed text-brand-warm-gray">{d.formDisclaimer}</p>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const { content } = useContent();
  const h = content.contactHero;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="main-content" tabIndex={-1}>
      <InnerPageHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Contact Us' },
        ]}
        title={h.title}
        titleAccent={h.titleAccent}
        titleSuffix={h.titleSuffix}
        description={h.description}
      />
      <ContactInfo />
      <FAQ />
    </main>
  );
}
